import { useState } from 'react'

import { isEmpty, isEqual, uniq } from 'lodash-es'

import { useGenAiHighRiskFlow } from './useGenAiHighRiskFlow'
import { useRemoveMatchedPreference } from './useRemoveMatchedPreference'
import { GenAiRiskLevel, ParsePreferenceRequest, ParsePreferenceResponse } from '../../../models'
import { isValidCustomPreferencesTextInput } from '../../common/utils/validationUtils'
import {
  MEMBER_PREFERENCE_IDS,
  MEMBER_PREFERENCE_NAMES,
  ProviderPreferenceValues,
} from '../../memberPreferences/constants'
import { MemberPreferencesForUserV2, ParsedPreference, ParsedPreferenceKey } from '../../memberPreferences/types'
import {
  convertProviderPreferenceValuesToParsedPreference,
  hasDisplayableParsedPreferences,
  sortByMetadataOrder,
} from '../../memberPreferences/utils'

/** Props grouped for the CustomPreferencesModal component */
export interface CustomPreferencesModalHookProps {
  isVisible: boolean
  onCancel: () => void
  onSubmit: () => void | Promise<void>
  matchedCustomPreferences: ParsedPreference
  isCustomPreferencesLoading: boolean
  isCustomPreferencesError: boolean
  onMeetMatches: () => void
  onRemoveParsedPreference: ({
    preferenceKey,
    valueId,
  }: {
    preferenceKey: ParsedPreferenceKey
    valueId: string | undefined
  }) => void
  viewMatchedPreferences: boolean
  onBackToInput: () => void
  isHighRiskModalVisible: boolean
  onDismissHighRiskModal: () => void
  isHighRisk: boolean
  hasAcknowledgedHighRisk: boolean
  onCustomPreferencesTextChange: (text: string) => void
}

export interface UseCustomPreferencesModalArgs {
  // Shared state from parent hook
  currentPreferences: ProviderPreferenceValues
  setCurrentPreferences: React.Dispatch<React.SetStateAction<ProviderPreferenceValues>>
  memberPreferencesV2: MemberPreferencesForUserV2 | null
  userId: string
  selectedSearchId: string | null
  shouldShowAvailabilityPreference: boolean
  isCustomPreferencesEnabled: boolean
  isSearchingForCoaching: boolean
  isSearchingForAdultTherapy: boolean

  // Redux actions
  postParsePreference?: ({
    lyraId,
    data,
    isSearchingForAdultTherapy,
    isSearchingForCoaching,
  }: {
    lyraId: string
    data: ParsePreferenceRequest
    isSearchingForAdultTherapy?: boolean
    isSearchingForCoaching?: boolean
  }) => Promise<ParsePreferenceResponse>
  parsePreference?: { data: ParsedPreference; id: string | null; riskLevel: GenAiRiskLevel | null }
  saveMemberPreferenceForUserV2: ({
    lyraId,
    data,
  }: {
    lyraId: string
    data: MemberPreferencesForUserV2
  }) => Promise<void>
  setUserMemberPreferencesV2?: (data: MemberPreferencesForUserV2) => void
  setParsePreferenceData?: (payload: {
    data: ParsedPreference
    id: string | null
    riskLevel: GenAiRiskLevel | null
  }) => void

  // Callbacks
  onConfirmPreferences?: () => void
  onNavigateToSearchCareRedirect?: () => void
  onProviderPreferenceChange: (newPreferenceValues: ProviderPreferenceValues) => Promise<void>
  getEmptyPreferenceState: ({
    shouldShowAvailability,
    isCustomPreferencesEnabled,
  }: {
    shouldShowAvailability: boolean
    isCustomPreferencesEnabled: boolean
  }) => ProviderPreferenceValues
  setResetDisabled: React.Dispatch<React.SetStateAction<boolean>>
}

export interface UseCustomPreferencesModalReturn {
  setIsCustomPreferencesModalVisible: React.Dispatch<React.SetStateAction<boolean>>
  customPreferencesModalProps: CustomPreferencesModalHookProps
}

/**
 * Hook to manage custom preferences modal state and logic.
 * Handles GenAI parsing, preference merging, error states, and high-risk flow.
 */
export const useCustomPreferencesModal = ({
  currentPreferences,
  setCurrentPreferences,
  memberPreferencesV2,
  userId,
  selectedSearchId,
  shouldShowAvailabilityPreference,
  isCustomPreferencesEnabled,
  isSearchingForCoaching,
  isSearchingForAdultTherapy,
  postParsePreference,
  parsePreference,
  saveMemberPreferenceForUserV2,
  setUserMemberPreferencesV2,
  setParsePreferenceData,
  onConfirmPreferences,
  onNavigateToSearchCareRedirect,
  onProviderPreferenceChange,
  getEmptyPreferenceState,
  setResetDisabled,
}: UseCustomPreferencesModalArgs): UseCustomPreferencesModalReturn => {
  // Derive values from parsePreference object
  const isHighRisk = parsePreference?.riskLevel === GenAiRiskLevel.RISK_LEVEL_YELLOW

  // Modal visibility state
  const [isCustomPreferencesModalVisible, setIsCustomPreferencesModalVisible] = useState(false)

  // Local loading state for custom preferences API call
  const [isCustomPreferencesLoading, setIsCustomPreferencesLoading] = useState(false)
  // Local error state for custom preferences API call
  const [isCustomPreferencesError, setIsCustomPreferencesError] = useState(false)
  // Tracks whether we're viewing results (stays true even if user removes all preferences)
  const [isViewingMatchedPreferences, setIsViewingMatchedPreferences] = useState(false)

  // GenAI high risk flow management - extracted to dedicated hook
  const {
    isGenAiHighRiskModalVisible: isHighRiskModalVisible,
    isReopeningAfterGenAiHighRisk: isReopeningAfterHighRisk,
    hasAcknowledgedHighRisk,
    onGenAiHighRiskDetected,
    onDismissGenAiHighRiskModal: onDismissHighRiskModal,
  } = useGenAiHighRiskFlow({
    isSearchingForCoaching,
    isCustomPreferencesModalVisible,
    onNavigateToSearchCareRedirect,
    onReopenCustomPreferencesModal: () => setIsCustomPreferencesModalVisible(true),
    onPrepareResultsView: () => setIsViewingMatchedPreferences(true),
    onCloseCustomPreferencesModal: () => {
      setIsCustomPreferencesModalVisible(false)
      setIsViewingMatchedPreferences(false) // Reset to input state when closing
    },
  })

  // Derive matchedCustomPreferences from currentPreferences to show merged state (saved + parsed)
  // This ensures the success modal displays all preferences, not just the AI-parsed ones
  // Also handles error case: if API fails but accordion has preferences, show those as fallback
  // Show merged state when viewing results (success or error with fallback to accordion)
  const matchedCustomPreferences = isViewingMatchedPreferences
    ? convertProviderPreferenceValuesToParsedPreference({
        providerPreferenceValues: currentPreferences,
        isSearchingForAdultTherapy,
      })
    : {}

  const onSubmitCustomPreferences = async () => {
    // If custom text is empty, close modal and skip API call
    if (!currentPreferences.customFreeTextPreference) {
      setIsViewingMatchedPreferences(false)
      setIsCustomPreferencesModalVisible(false)
      return
    }
    // Validate custom preferences text input - UI handles error display, but we still prevent API call
    if (!isValidCustomPreferencesTextInput(currentPreferences.customFreeTextPreference)) {
      return
    }
    if (!postParsePreference || !selectedSearchId) {
      return
    }

    setIsCustomPreferencesLoading(true)
    setIsCustomPreferencesError(false)

    const response = await postParsePreference({
      lyraId: userId,
      data: {
        searchId: selectedSearchId,
        rawFreeText: currentPreferences.customFreeTextPreference,
      },
      isSearchingForAdultTherapy,
      isSearchingForCoaching,
    }).catch(() => {
      setIsCustomPreferencesLoading(false)

      // Check if we have accordion preferences to fall back to
      const hasAccordionPreferences = hasDisplayableParsedPreferences(
        convertProviderPreferenceValuesToParsedPreference({
          providerPreferenceValues: currentPreferences,
          isSearchingForAdultTherapy,
        }),
      )

      // Only show error if we have no accordion preferences to display
      setIsCustomPreferencesError(!hasAccordionPreferences)
      setIsViewingMatchedPreferences(true)
      return null
    })

    if (!response) {
      return
    }

    const { preferences: parsedPrefs, riskLevel } = response

    // Auto-select preferences in accordions based on parsed preferences
    if (!isEmpty(parsedPrefs)) {
      const updatedPreferences: ProviderPreferenceValues = {
        ...currentPreferences,
        gender: uniq([...currentPreferences.gender, ...(parsedPrefs.gender ?? [])]),
        ethnicities: sortByMetadataOrder(
          uniq([...currentPreferences.ethnicities, ...(parsedPrefs.ethnicities ?? [])]),
          MEMBER_PREFERENCE_NAMES.ETHNICITIES,
        ),
        religions: sortByMetadataOrder(
          uniq([...currentPreferences.religions, ...(parsedPrefs.religions ?? [])]),
          MEMBER_PREFERENCE_NAMES.RELIGIONS,
        ),
        availabilities:
          uniq([...(currentPreferences.availabilities ?? []), ...(parsedPrefs.availabilities ?? [])]).length > 0
            ? uniq([...(currentPreferences.availabilities ?? []), ...(parsedPrefs.availabilities ?? [])])
            : null,
        isLgbtqia:
          parsedPrefs.isLgbtqia && !currentPreferences.isLgbtqia.includes(MEMBER_PREFERENCE_IDS['LGBTQIA+'])
            ? [...currentPreferences.isLgbtqia, MEMBER_PREFERENCE_IDS['LGBTQIA+']]
            : currentPreferences.isLgbtqia,
        isExperiencedWithVeterans:
          parsedPrefs.isExperiencedWithVeterans &&
          !currentPreferences.isExperiencedWithVeterans.includes(MEMBER_PREFERENCE_IDS.EXPERIENCE_WORKING_WITH_VETERANS)
            ? [...currentPreferences.isExperiencedWithVeterans, MEMBER_PREFERENCE_IDS.EXPERIENCE_WORKING_WITH_VETERANS]
            : currentPreferences.isExperiencedWithVeterans,
        meetingSetting: uniq([
          ...currentPreferences.meetingSetting,
          ...(parsedPrefs.preferredAppointmentType ?? []),
          ...(parsedPrefs.preferredSessionFormat ?? []),
        ]),
      }
      setCurrentPreferences(updatedPreferences)

      // Save the merged preferences to backend
      await onProviderPreferenceChange(updatedPreferences)
    }
    setIsCustomPreferencesLoading(false)

    // Check for high risk and trigger appropriate flow
    if (riskLevel === GenAiRiskLevel.RISK_LEVEL_YELLOW) {
      onGenAiHighRiskDetected()
    } else {
      setIsViewingMatchedPreferences(true)
    }
  }

  const onMeetMatches = () => {
    setIsCustomPreferencesModalVisible(false)
    setIsViewingMatchedPreferences(false)
    onConfirmPreferences?.()
  }

  // Hook for preference removal + persistence (shared with entry point flow)
  const removeMatchedPreference = useRemoveMatchedPreference({
    memberPreferencesV2,
    onSavePreferences: async (updatedPreferences) => {
      await saveMemberPreferenceForUserV2({ lyraId: userId, data: updatedPreferences })
      setUserMemberPreferencesV2?.(updatedPreferences)
    },
    setCurrentPreferences,
  })

  const onRemoveParsedPreference = ({
    preferenceKey,
    valueId,
  }: {
    preferenceKey: ParsedPreferenceKey
    valueId: string | undefined
  }) => {
    removeMatchedPreference({ preferenceKey, valueId })
  }

  const onBackToInput = () => {
    setIsViewingMatchedPreferences(false)
    setIsCustomPreferencesError(false)
  }

  const onCancelCustomPreferencesModal = () => {
    // Ignore cancel if we're reopening after high risk dismissal (prevents race condition with modal close animation)
    if (isReopeningAfterHighRisk) {
      return
    }

    setIsCustomPreferencesModalVisible(false)
    setIsViewingMatchedPreferences(false)
    // Clear stale GenAI data while preserving parsePreferenceId for provider recommendations
    setParsePreferenceData?.({ data: {}, id: parsePreference?.id ?? null, riskLevel: null })
  }

  const onCustomPreferencesTextChange = (text: string) => {
    const updatedPreferences = { ...currentPreferences, customFreeTextPreference: text }
    setCurrentPreferences(updatedPreferences)

    // Update reset button state
    const isEmptyPreferencesState = isEqual(
      updatedPreferences,
      getEmptyPreferenceState({
        shouldShowAvailability: shouldShowAvailabilityPreference,
        isCustomPreferencesEnabled: isCustomPreferencesEnabled,
      }),
    )
    setResetDisabled(isEmptyPreferencesState)
  }

  // Group all custom preferences modal props for easier passing to components
  const customPreferencesModalProps: CustomPreferencesModalHookProps = {
    isVisible: isCustomPreferencesModalVisible,
    onCancel: onCancelCustomPreferencesModal,
    onSubmit: onSubmitCustomPreferences,
    matchedCustomPreferences,
    isCustomPreferencesLoading,
    isCustomPreferencesError,
    onMeetMatches,
    onRemoveParsedPreference,
    viewMatchedPreferences: isViewingMatchedPreferences,
    onBackToInput,
    isHighRiskModalVisible,
    onDismissHighRiskModal,
    isHighRisk,
    hasAcknowledgedHighRisk,
    onCustomPreferencesTextChange,
  }

  return {
    setIsCustomPreferencesModalVisible,
    customPreferencesModalProps,
  }
}
